// RTK Query slice for restaurant website/page management
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseQuery";

export const restaurantApi = createApi({
  reducerPath: "restaurantApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  keepUnusedDataFor: 300, // Cache for 5 minutes
  tagTypes: ["RestaurantPage", "RestaurantImages", "Menus"],
  endpoints: (builder) => ({
    /** Get restaurant page data */
    getRestaurantPage: builder.query({
      query: () => ({
        url: "api/restaurant-page",
        method: "GET",
      }),
      providesTags: ["RestaurantPage"],
    }),

    /** Update restaurant page data */
    updateRestaurantPage: builder.mutation({
      query: (body) => ({
        url: "api/restaurant-page",
        method: "POST",
        body,
      }),
      invalidatesTags: ["RestaurantPage"],
    }),

    /** Upload restaurant image */
    uploadRestaurantImage: builder.mutation({
      query: ({ section, type, formData }) => ({
        url: `api/restaurant-page-images/${section}/${type}`,
        method: "POST",
        body: formData,
        // Don't set Content-Type header, let browser set it with boundary for multipart
        headers: {},
      }),
      invalidatesTags: ["RestaurantImages", "RestaurantPage"],
    }),

    /** Generate AI description using Gemini */
    generateAiDescription: builder.mutation({
      query: (body) => ({
        url: "api/auth-gemini",
        method: "POST",
        body,
      }),
    }),

    /** Get menus */
    getMenus: builder.query({
      query: () => ({
        url: "api/menus",
        method: "GET",
      }),
      providesTags: ["Menus"],
    }),

    /** Create menu */
    createMenu: builder.mutation({
      query: (body) => ({
        url: "api/menus",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Menus"],
    }),

    /** Update menu */
    updateMenu: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/menus/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["Menus"],
    }),

    /** Delete menu */
    deleteMenu: builder.mutation({
      query: (id) => ({
        url: `api/menus/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Menus"],
    }),
  }),
});

export const {
  useGetRestaurantPageQuery,
  useUpdateRestaurantPageMutation,
  useUploadRestaurantImageMutation,
  useGenerateAiDescriptionMutation,
  useGetMenusQuery,
  useCreateMenuMutation,
  useUpdateMenuMutation,
  useDeleteMenuMutation,
} = restaurantApi;
